import React, { useState } from "react";
import {
  Sparkles,
  Award,
  TrendingUp,
  Brain,
  Briefcase,
  GraduationCap,
  Building,
  CheckCircle2,
  ShieldCheck,
  Mail,
  MapPin,
  Building2,
  ExternalLink,
  Linkedin,
  Zap,
  Users,
} from "lucide-react";
import { motion } from "framer-motion";

export default function AboutUs() {
  const [activeSegment, setActiveSegment] = useState("tpo");

  const milestones = [
    {
      year: "2024",
      title: "Founding & Incubation",
      desc: "EduConnectPro was established by university placement cell veterans who were frustrated by the limitations of static spreadsheets and slow-loading legacy portals.",
      tag: "Origin",
    },
    {
      year: "2025",
      title: "Startup Odisha Partnership",
      desc: "Talent Connect has partnered with Startup Odisha to bridge the technology gap. Together, we have successfully collaborated with 2,000+ pioneering startups across the state over 3 structured operational phases to place high-performing college minds and build the future of Indian deeptech.",
      tag: "Strategic Alliance",
    },
    {
      year: "2025",
      title: "GSV Central Railway University Startup Honors",
      desc: "Recognized as a leading enterprise platform at the National Entrepreneurship Summit, validating our secure candidate-employer mapping model.",
      tag: "Top Innovator",
    },
    {
      year: "2026",
      title: "Scale to 250+ Institutions",
      desc: "Enabling seamless, high-velocity campus drives for over 2 Lac+ verified freshers and establishing real-time data syncing for 25,000+ jobs.",
      tag: "Industry Scale",
    },
  ];

  const segmentContent = {
    tpo: {
      title: "TPO Command Center",
      subtitle: "The Central Control Panel for College Placements",
      bullets: [
        "Establish precise academic filters including backlog limits, department-specific locks, and dynamic CGPA thresholds in two clicks.",
        "Bulk broadcast campaign notifications directly to student mobile phones via WhatsApp and Email.",
        "Export pre-verified compliance files structured directly for NAAC, NBA, and NIRF audits.",
        "Track live recruitment funnel stats and real-time shortlists across dozens of parallel drives.",
      ],
      tagline: "Empowering Placement Officers with SaaS-AI Automation",
      stat: "35+ Colleges Simplified",
      icon: (
        <Building style={{ width: "32px", height: "32px", color: "#FF5F14" }} />
      ),
    },
    student: {
      title: "Student Mobile Experience",
      subtitle: "The Live Career Companion (Freshment App)",
      bullets: [
        "Build beautifully parsed, standard-compliant ATS resumes in dynamic live sessions.",
        "Prepare for actual interview queries using AI Co-Pilot tailored directly to the specific job JD.",
        "Receive direct alerts for newly posted matching jobs and register with a single click.",
        "Track interview schedules, real-time feedback loops, and dynamic status updates in one place.",
      ],
      tagline: "Bridging the Skill Gap with AI Interview Readiness",
      stat: "96,000+ Active Installs",
      icon: (
        <GraduationCap
          style={{ width: "32px", height: "32px", color: "#FF5F14" }}
        />
      ),
    },
    recruiter: {
      title: "Recruiter Interface (TalentConnect)",
      subtitle: "Zero-friction Employer Engagement Portal",
      bullets: [
        "Query a university's entire student database using precise eligibility search metrics.",
        "Download highly structured, pre-verified candidate resume bundles in a single click.",
        "Organize multi-stage assessment tracks, shortlist pools, and dynamic candidate feedback.",
        "Publish new hiring drives and view interest pipelines instantly with zero fee commissions.",
      ],
      tagline: "Direct Access to Pre-Verified, High-Caliber Freshers",
      stat: "25,000+ High-Value Jobs Posted",
      icon: (
        <Briefcase
          style={{ width: "32px", height: "32px", color: "#FF5F14" }}
        />
      ),
    },
  };

  return (
    <div className=" position-relative" style={styles.page}>
      {/* Subtle Background Elements */}
      <div style={styles.bgGlowTopRight} />
      <div style={styles.bgGlowBottomLeft} />

      {/* Hero Section */}
      <div
        className="text-center mx-auto position-relative"
        style={styles.heroSection}
      >
        <motion.span
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="d-inline-flex align-items-center heroEyebrow hero-badge font-display"
          style={styles.heroEyebrow}
        >
          <Sparkles
            style={{ width: "14px", height: "14px", marginRight: "6px" }}
          />
          The EduConnectPro x TalentConnect Story
        </motion.span>

        <motion.h1
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          style={styles.heroTitle}
          className="font-display heroTitle"
        >
          Bridging Academic Learning &{" "}
          <span className="font-display" style={{ color: "#FF5F14" }}>
            Industry Hiring Expectation
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={styles.heroDesc}
          className="hero-desc"
        >
          EduConnectPro is an AI-powered Placement Intelligence Platform that
          empowers universities with AI CoPilot, employer engagement, campus
          recruitment preparation, skill-gap intelligence, interview readiness,
          and placement analytics—bridging the gap between academic learning and
          industry hiring expectations.
        </motion.p>
      </div>

      {/* Modern Bento Stat Grid */}
      <div
        className="  container  position-relative"
        style={styles.bentoGridWrapper}
      >
        <div className="row g-3 g-sm-4">
          <div className="col-6 col-lg-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="d-flex flex-column justify-content-between bentoCard h-100"
              style={styles.bentoCard}
            >
              <div className="font-display bentoLabel" style={styles.bentoLabel}>
                Verified Candidates
              </div>
              <div style={{ marginTop: "1rem" }}>
                <span className="font-display bentoStatDark" style={styles.bentoStatDark}>
                  2 Lac+
                </span>
                <p className="bentoSubtext" style={styles.bentoSubtext}>Active, verified freshers</p>
              </div>
            </motion.div>
          </div>

          <div className="col-6 col-lg-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="d-flex flex-column bentoCard justify-content-between h-100"
              style={styles.bentoCard}
            >
              <div
                className="font-display bentoLabel"
                style={{ ...styles.bentoLabel, color: "#FF5F14" }}
              >
                Industry Scale
              </div>
              <div style={{ marginTop: "1rem" }}>
                <span className="font-display bentoStatDark" style={styles.bentoStatOrange}>
                  25,000+
                </span>
                <p className="bentoSubtext" style={styles.bentoSubtext}>High-value jobs posted</p>
              </div>
            </motion.div>
          </div>

          <div className="col-6 col-lg-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="d-flex flex-column  bentoCard justify-content-between h-100"
              style={styles.bentoCard}
            >
              <div className="font-display bentoLabel" style={styles.bentoLabel}>
                Mobile Engagement
              </div>
              <div style={{ marginTop: "1rem" }}>
                <span className="font-display bentoStatDark" style={styles.bentoStatDark}>
                  96,000+
                </span>
                <p className="bentoSubtext" style={styles.bentoSubtext}>
                  Freshment app downloads by Students
                </p>
              </div>
            </motion.div>
          </div>

          <div className="col-6 col-lg-3">
            <motion.div
              whileHover={{ y: -5 }}
              className="d-flex flex-column bentoCard  justify-content-between h-100"
              style={styles.bentoCardOrange}
            >
              <div className="font-display bentoLabel" style={styles.bentoLabelOrange}>
                Data Integrity
              </div>
              <div style={{ marginTop: "1rem" }}>
                <span className="font-display bentoStatDark" style={styles.bentoStatWhite}>
                  100%
                </span>
                <p className="bentoSubtext" style={styles.bentoSubtextOrange}>Academic Roster Locks</p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* High-Impact Interactive Operational Pillars Section */}
      <div
        className="  container  position-relative"
        style={styles.pillarsWrapper}
      >
        <div className="text-center" style={{ marginBottom: "3rem" }}>
          <span className="font-display" style={styles.eyebrow}>
            THE PLATFORM IN MOTION
          </span>
          <h2 className="font-display sectionTitle" style={styles.sectionTitle}>
            Three-Way Real-Time Placement Portal Sync
          </h2>
          <p style={styles.sectionSubtext}>
            Explore how our integrated portal modules work in unison to
            eliminate placement friction.
          </p>
        </div>

        {/* Tab triggers */}
        <div
          className="d-flex   justify-content-center"
          style={{ marginBottom: "2.5rem" }}
        >
          <div
            className="d-inline-flex flex-wrap justify-content-center"
            style={styles.tabGroup}
          >
            <button
              className="font-display "
              onClick={() => setActiveSegment("tpo")}
              style={{
                ...styles.tabButton,
                ...(activeSegment === "tpo"
                  ? styles.tabButtonActive
                  : styles.tabButtonInactive),
              }}
            >
              TPO Command Center
            </button>
            <button
              className="font-display"
              onClick={() => setActiveSegment("student")}
              style={{
                ...styles.tabButton,
                ...(activeSegment === "student"
                  ? styles.tabButtonActive
                  : styles.tabButtonInactive),
              }}
            >
              Student Portal (Freshment App)
            </button>
            <button
              className="font-display"
              onClick={() => setActiveSegment("recruiter")}
              style={{
                ...styles.tabButton,
                ...(activeSegment === "recruiter"
                  ? styles.tabButtonActive
                  : styles.tabButtonInactive),
              }}
            >
              Recruiter Hub (TalentConnect)
            </button>
          </div>
        </div>

        {/* Segment Showcase Card */}
        <div
          className="row position-relative align-items-center showcaseCard"
          style={styles.showcaseCard}
        >
          <div style={styles.showcaseCorner} />

          <div
            className="col-12 col-lg-7"
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <div className="d-flex align-items-center" style={{ gap: "1rem" }}>
              <div style={styles.showcaseIconBox}>
                {segmentContent[activeSegment].icon}
              </div>
              <div>
                <span
                  className="font-display tagline"
                  style={styles.showcaseTagline}
                >
                  {segmentContent[activeSegment].tagline}
                </span>
                <h3 className="font-display title" style={styles.showcaseTitle}>
                  {segmentContent[activeSegment].title}
                </h3>
              </div>
            </div>

            <p style={styles.showcaseSubtitle}>
              {segmentContent[activeSegment].subtitle}
            </p>

            <ul
              className="list-unstyled"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.875rem",
                paddingTop: "0.5rem",
                margin: 0,
              }}
            >
              {segmentContent[activeSegment].bullets.map((bullet, bIdx) => (
                <li
                  key={bIdx}
                  className="d-flex subText10px align-items-start"
                  style={{
                    gap: "0.75rem",
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                    color: "#475569",
                    lineHeight: 1.625,
                  }}
                >
                  <div style={styles.bulletCheckWrapper}>
                    <CheckCircle2
                      style={{
                        width: "1.25rem",
                        height: "1.25rem",
                        color: "#059669",
                      }}
                    />
                  </div>
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="col-12 col-lg-5 position-relative mt-4 mt-lg-0"
            style={styles.statBlock}
          >
            <div style={styles.statBlockBadgeWrapper}>
              <span
                className="font-mono statBlockBadge"
                style={styles.statBlockBadge}
              >
                Active Sync Mode
              </span>
            </div>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
            >
              <span className="font-mono" style={styles.statBlockLabel}>
                Real-Time Operational Met
              </span>
              <div className="font-display stat" style={styles.statBlockValue}>
                {segmentContent[activeSegment].stat}
              </div>
              <p style={styles.statBlockDesc}>
                Seamless data pipelines map candidate updates directly across
                our entire visual dashboard network instantly.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Incubation Trust / Strategic Backing Banner */}
      <div className=" container  position-relative" style={styles.trustBanner}>
        <div style={styles.trustGlow1} />
        <div style={styles.trustGlow2} />

        <div
          className="row position-relative align-items-center"
          style={{ zIndex: 10 }}
        >
          <div
            className="col-12 col-lg-5 mb-4 mb-lg-0"
            style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
          >
            <div>
              <span className="font-mono subText10px" style={styles.trustEyebrow}>
                Strategic Backing & Incubation
              </span>
            </div>
            <h3 className="font-display trustTitle" style={styles.trustTitle}>
              Backed by Elite Technological & Administrative Ecosystems
            </h3>
            <p className="subText10px" style={styles.trustText}>
              EduConnectPro is supported by leading state government initiatives
              and institutional structures, validating our academic and
              technological reach.
            </p>
          </div>

          <div className="col-12 col-lg-7">
            <div className="row g-3">
              <div className="col-12 col-sm-4 ">
                <div className=" card-hover" style={styles.trustCard}>
                  <Award
                    style={{
                      width: "1.75rem",
                      height: "1.75rem",
                      color: "#FF5F14",
                      marginBottom: "0.75rem",
                    }}
                  />
                  <h4 className="font-display" style={styles.trustCardTitle}>
                    iHub Gujarat
                  </h4>
                  <p style={styles.trustCardText}>
                    Incubated and monitored under the Government of Gujarat's
                    premier innovation initiative.
                  </p>
                </div>
              </div>

              <div className="col-12 col-sm-4">
                <div className=" card-hover" style={styles.trustCard}>
                  <TrendingUp
                    style={{
                      width: "1.75rem",
                      height: "1.75rem",
                      color: "#FF5F14",
                      marginBottom: "0.75rem",
                    }}
                  />
                  <h4 className="font-display" style={styles.trustCardTitle}>
                    Startup Odisha
                  </h4>
                  <p style={styles.trustCardText}>
                    Recognized for pioneering unified academic recruiting
                    software and placement orchestration.
                  </p>
                </div>
              </div>

              <div className="col-12 col-sm-4">
                <div className=" card-hover" style={styles.trustCard}>
                  <Brain
                    style={{
                      width: "1.75rem",
                      height: "1.75rem",
                      color: "#FF5F14",
                      marginBottom: "0.75rem",
                    }}
                  />
                  <h4 className="font-display" style={styles.trustCardTitle}>
                    GSV Central Railway University
                  </h4>
                  <p style={styles.trustCardText}>
                    Accelerated and recognized at the National Entrepreneurship
                    Summit for tech excellence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Improved Operational Story Section - High-Impact Grid Layout */}
      <div className=" container position-relative" style={styles.storyWrapper}>
        <div className="row align-items-center" style={{ gap: "3rem 0" }}>
          <div
            className="col-12 col-lg-6"
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <div>
              <span
                className="d-inline-flex font-mono storyEyebrow align-items-center"
                style={styles.storyEyebrow}
              >
                <Zap
                  style={{ width: "14px", height: "14px", marginRight: "6px" }}
                />
                HOW WE ARE BRIDGING THE GAP
              </span>
            </div>

            <h2 className="font-display storyTitle" style={styles.storyTitle}>
              Created by Placement Officers, <br />
              <span className="font-display" style={{ color: "#FF5F14" }}>
                Architected for Recruiters
              </span>
            </h2>

            <p className="storyMainText" style={styles.storyMainText}>
              As placement cell directors and enterprise SaaS developers, we
              lived the friction of campus hiring firsthand. Universities
              struggled under the administrative burden of spreadsheet-based
              tracking and legacy portals, while recruiters wrestled with
              unverified resumes and manual candidate filtering.
            </p>

            <p  className="storySubText" style={styles.storySubText}>
              We engineered a unified framework to serve as a high-speed, direct
              conduit. By integrating automated ATS resume construction, robust
              eligibility matching, and tamper-proof academic roster locks, we
              protect colleges from administrative fatigue while granting
              recruiters friction-free access to high-caliber, pre-verified
              talent.
            </p>

            <div className="row g-3" style={{ paddingTop: "1rem" }}>
              <div className="col-12 col-sm-6">
                <div
                  className="d-flex align-items-start"
                  style={{ gap: "0.75rem" }}
                >
                  <CheckCircle2
                    style={{
                      width: "1.25rem",
                      height: "1.25rem",
                      color: "#FF5F14",
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  />
                  <div>
                    <h4
                      className="font-display"
                      style={styles.storyFeatureTitle}
                    >
                      Spreadsheet Elimination
                    </h4>
                    <p style={styles.storyFeatureText}>
                      Automated candidate matching saves hundreds of operational
                      hours.
                    </p>
                  </div>
                </div>
              </div>
              <div className="col-12 col-sm-6">
                <div
                  className="d-flex align-items-start"
                  style={{ gap: "0.75rem" }}
                >
                  <CheckCircle2
                    style={{
                      width: "1.25rem",
                      height: "1.25rem",
                      color: "#FF5F14",
                      flexShrink: 0,
                      marginTop: "2px",
                    }}
                  />
                  <div>
                    <h4
                      className="font-display"
                      style={styles.storyFeatureTitle}
                    >
                      Verified Credential Locks
                    </h4>
                    <p style={styles.storyFeatureText}>
                      100% accurate student CGPA and backlog statuses enforced
                      at source.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className="col-12 col-lg-6"
            style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
          >
            <motion.div
              whileHover={{ x: 6 }}
              className="position-relative"
              style={styles.solutionCard}
            >
              <div style={styles.solutionCorner} />
              <div className="d-flex align-items-start" style={{ gap: "1rem" }}>
                <div style={styles.solutionIconBox}>
                  <GraduationCap
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      color: "#FF5F14",
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-display" style={styles.solutionTitle}>
                    Academic Empowerment (EduConnectPro)
                  </h3>
                  <p className="subText10px" style={styles.solutionText}>
                    Empowering university placement cells with structural roster
                    controls, direct bulk communications, and compliant export
                    modules tailored for regulatory NIRF and NAAC audits.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ x: 6 }}
              className="position-relative"
              style={styles.solutionCard}
            >
              <div style={styles.solutionCorner} />
              <div className="d-flex align-items-start" style={{ gap: "1rem" }}>
                <div style={styles.solutionIconBox}>
                  <Briefcase
                    style={{
                      width: "1.5rem",
                      height: "1.5rem",
                      color: "#FF5F14",
                    }}
                  />
                </div>
                <div>
                  <h3 className="font-display" style={styles.solutionTitle}>
                    Employer Optimization (TalentConnect)
                  </h3>
                  <p  className="subText10px" style={styles.solutionText}>
                    Providing direct access to verified candidate databases,
                    enabling instant single-click batch resume downloads,
                    zero-commission placement campaigns, and multi-stage
                    pipeline controls.
                  </p>
                </div>
              </div>
            </motion.div>

            <div
              className="d-flex align-items-start"
              style={styles.validationBanner}
            >
              <Award
                className="icon-bounce"
                style={{
                  width: "1.5rem",
                  height: "1.5rem",
                  color: "#FF5F14",
                  flexShrink: 0,
                  marginTop: "2px",
                }}
              />
              <div>
                <h4 className="font-display" style={styles.validationTitle}>
                  National Scale Validation Accolade
                </h4>
                <p className="subText10px" style={styles.validationText}>
                  Awarded top recognition for dynamic, secure placement system
                  standards. Trusted by academic bodies to execute large-scale,
                  high-integrity university placement drives across India.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Compliance & Startup Verification / Leadership & Corporate Registry */}
      <div
        className=" container  position-relative"
        style={styles.governanceWrapper}
      >
        <div className="text-center" style={{ marginBottom: "4rem" }}>
          <span
            className="font-display"
            style={{
              ...styles.eyebrow,
              display: "block",
              marginBottom: "0.25rem",
            }}
          >
            GOVERNANCE & TRUST
          </span>
          <h2 className="font-display" style={styles.sectionTitle}>
            Compliance & Startup Verification
          </h2>
          <p className="subText10px" style={styles.sectionSubtext}>
            Freshment is a fully structured, operational startup directed under
            the highest compliance and governance standards in India.
          </p>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-7">
            <motion.div
              whileHover={{ y: -4 }}
              className="d-flex flex-column justify-content-between position-relative h-100"
              style={styles.leadershipCard}
            >
              <div style={styles.leadershipCorner} />

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                <div>
                  <span className="font-mono" style={styles.leadershipLabel}>
                    FOUNDER & EXECUTIVE DIRECTOR
                  </span>
                  <h3 className="font-display" style={styles.leadershipName}>
                    Tejal Sonaiya
                  </h3>
                  <p className="font-mono" style={styles.leadershipRole}>
                    Founder & CEO, GRADCONNECT INNOVATIONS PRIVATE LIMITED
                  </p>
                </div>

                <p className="subText10px" style={styles.leadershipBio}>
                  Under the governance and strategic leadership of Tejal
                  Sonaiya, GRADCONNECT INNOVATIONS PRIVATE LIMITED is committed
                  to democratizing educational placement gateways. Tejal drives
                  platform compliance, national startup strategies (such as the
                  Startup Odisha Drive), and enterprise relations to provide a
                  reliable career entry environment for 10M+ candidates across
                  underserved regional college sectors.
                </p>
              </div>

              <div style={{ paddingTop: "2rem" }}>
                <a
                  href="https://www.linkedin.com"
                  target="_blank"
                  rel="noreferrer"
                  className="d-inline-flex align-items-center text-decoration-none linkedinButton"
                  style={styles.linkedinButton}
                >
                  <Linkedin style={{ width: "16px", height: "16px" }} />
                  <span className="font-display">Verify LinkedIn Profile</span>
                  <ExternalLink
                    style={{ width: "14px", height: "14px", opacity: 0.8 }}
                  />
                </a>
              </div>
            </motion.div>
          </div>

          <div className="col-12 col-lg-5">
            <motion.div
              whileHover={{ y: -4 }}
              className="d-flex flex-column justify-content-between position-relative h-100"
              style={styles.registryCard}
            >
              <div style={styles.registryCorner} />

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.5rem",
                }}
              >
                <div>
                  <span className="font-mono" style={styles.registryLabel}>
                    OFFICIAL ENTITY
                  </span>
                  <h3 className="font-display" style={styles.registryTitle}>
                    Freshment Corporate Registry
                  </h3>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <div
                    className="d-flex align-items-start"
                    style={{ gap: "0.75rem" }}
                  >
                    <div style={styles.registryIconBox}>
                      <Building2
                        style={{
                          width: "1rem",
                          height: "1rem",
                          color: "#FF5F14",
                        }}
                      />
                    </div>
                    <div>
                      <span style={styles.registryFieldLabel}>
                        Registered Entity Name
                      </span>
                      <p style={styles.registryFieldValue}>
                        GRADCONNECT INNOVATIONS PRIVATE LIMITED
                      </p>
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-start"
                    style={{ gap: "0.75rem" }}
                  >
                    <div style={styles.registryIconBox}>
                      <MapPin
                        style={{
                          width: "1rem",
                          height: "1rem",
                          color: "#FF5F14",
                        }}
                      />
                    </div>
                    <div>
                      <span style={styles.registryFieldLabel}>
                        CORPORATE ADDRESS
                      </span>
                      <p style={styles.registryAddress}>
                        B-519, RK Iconic Sheetal Park, Puneet Naga Bajrang,
                        Rajkot Bhomeshwar Plot, Rajkot- 360006, Gujarat
                      </p>
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-start"
                    style={{ gap: "0.75rem" }}
                  >
                    <div style={styles.registryIconBox}>
                      <Mail
                        style={{
                          width: "1rem",
                          height: "1rem",
                          color: "#FF5F14",
                        }}
                      />
                    </div>
                    <div>
                      <span style={styles.registryFieldLabel}>
                        OFFICIAL ELECTRONIC CONTACT
                      </span>
                      <a
                        href="mailto:contact@freshmentapp.com"
                        style={styles.registryEmail}
                      >
                        contact@freshmentapp.com
                      </a>
                    </div>
                  </div>

                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "0.625rem", paddingTop: "0.5rem" }}
                  >
                    <span
                      className="d-inline-flex font-mono align-items-center"
                      style={styles.statusBadge}
                    >
                      <ShieldCheck
                        style={{
                          width: "14px",
                          height: "14px",
                          marginRight: "6px",
                        }}
                      />
                      Status: Active Compliant Startup
                    </span>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "2rem",
                  paddingTop: "1.5rem",
                  borderTop: "1px solid #1e293b",
                }}
              >
                <span className="font-mono" style={styles.registryFooterLabel}>
                  Ecosystem Verification Channel
                </span>
                <p className="subText10px" style={styles.registryFooterText}>
                  We are fully operational and verified under state/national
                  incubator ecosystems. If you require further legal compliance
                  metrics or validation details, please connect with our
                  Corporate Coordinator group.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Milestones / Journey Timeline */}
      <div
        className=" container  position-relative"
        style={styles.milestonesWrapper}
      >
        <div className="text-center" style={{ marginBottom: "4rem" }}>
          <span
            className="font-display"
            style={{
              ...styles.eyebrow,
              display: "block",
              marginBottom: "0.25rem",
            }}
          >
            THE MILESTONE LOG
          </span>
          <h2 className="font-display" style={styles.sectionTitle}>
            Evolving the Placement Intelligence Standard
          </h2>
        </div>

        <div className="row g-4">
          {milestones.map((mile, i) => (
            <div key={i} className="col-12 col-sm-6 col-lg-3">
              <div
                className="d-flex flex-column justify-content-between h-100 position-relative milestone-card-hover"
                style={styles.milestoneCard}
              >
                <div
                  className="milestone-top-bar"
                  style={styles.milestoneTopBar}
                />
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="font-display" style={styles.milestoneYear}>
                      {mile.year}
                    </span>
                    <span className="font-mono" style={styles.milestoneTag}>
                      {mile.tag}
                    </span>
                  </div>
                  <h4 className="font-display" style={styles.milestoneTitle}>
                    {mile.title}
                  </h4>
                  <p className="subText10px" style={styles.milestoneDesc}>{mile.desc}</p>
                </div>
              </div>
            </div>
          ))}

          <style>{`
  .milestone-card-hover {
    overflow: hidden;
    transition: border-color 0.3s ease, box-shadow 0.3s ease;
  }
  .milestone-card-hover:hover {
    border-color: #cbd5e1 !important;
    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1) !important;
  }

  .milestone-top-bar {
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  .milestone-card-hover:hover .milestone-top-bar {
    transform: scaleX(1);
  }
      .heroTitle {    font-size: 3.75rem;    }
    .hero-desc{        font-size: 1rem;    }
    .hero-badge{           gap:0.5rem;    }
  .bentoCard{    padding: 2rem;  }
    .sectionTitle{font-size:2rem;}
    .showcaseCard{padding:3rem;}


  @media (max-width:960px)
  {
    .heroTitle {    font-size: 2.75rem;    }
    .hero-desc{            font-size:12px;       }

  }
  @media (max-width:640px)
  {
    .heroTitle {    font-size: 2rem;    }
    .hero-desc{            font-size:10px;    }
    .storyTitle{            font-size:30px !important;    }
    .bentoCard{    padding: 1rem;  }
    .storyMainText{            font-size:11px  !important;    }
    .storySubText{            font-size:10px !important;    }

  }
  @media (max-width:540px)
  {            
     .sectionTitle{font-size:1.5rem;}
     .trustTitle{font-size:24px !important;}
     .showcaseCard{padding:2rem}
    .storyTitle{            font-size:27px !important;    }

  }
  @media (max-width:480px)
  {
    .hero-desc{            font-size:9px;    }
    .hero-badge{              gap:0;            }
  .showcaseCard{padding:0.50rem;}
  .heroEyebrow{    font-size: 8px !important;}
  .tagline{font-size: 8px !important;}
  .title{font-size: 17px !important;}
  .subText10px{font-size: 10px !important;}
  .stat{font-size: 20px !important;}
  .statBlockBadge{display: none !important;}
     .trustTitle{font-size:18px !important;}
    .storyTitle{            font-size:24px !important;    }

    .bentoLabel {font-size:10px !important;}
    .bentoStatDark {font-size:1.25rem !important;}
    .bentoSubtext {font-size:9px !important;}


  }
`}</style>
        </div>
      </div>

      <style>{`
      @keyframes iconBounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-6px);
  }
}

.icon-bounce {
  animation: iconBounce 1s ease-out infinite;
}
  .linkedinButton:hover{
        background-color:#008bd3 !important;
  }
      `}</style>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    color: "#0f172a",
    padding: "6rem 1rem",
    fontFamily: "system-ui, -apple-system, sans-serif",
    overflow: "hidden",
  },
  bgGlowTopRight: {
    position: "absolute",
    top: "2.5rem",
    right: "2.5rem",
    width: "24rem",
    height: "24rem",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.05)",
    filter: "blur(120px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  bgGlowBottomLeft: {
    position: "absolute",
    bottom: "2.5rem",
    left: "2.5rem",
    width: "28rem",
    height: "28rem",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 95, 20, 0.03)",
    filter: "blur(150px)",
    pointerEvents: "none",
    zIndex: 0,
  },
  heroSection: {
    maxWidth: "56rem",
    marginBottom: "5rem",
    zIndex: 10,
  },
  heroEyebrow: {
    padding: "0.375rem 1rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#FF5F14",
    fontSize: "0.75rem",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: "1rem",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  heroTitle: {
    fontWeight: "900",
    color: "#020617",
    letterSpacing: "-0.025em",
    marginBottom: "1.5rem",
    lineHeight: 1.1,
  },
  heroDesc: {
    color: "#475569",
    lineHeight: 1.625,
    fontWeight: "600",
    maxWidth: "48rem",
    margin: "0 auto",
  },
  bentoGridWrapper: {
    marginBottom: "7rem",
    zIndex: 10,
  },
  bentoCard: {
    borderRadius: "1.5rem",
    backgroundColor: "white",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
    transition: "transform",
  },
  bentoCardOrange: {
    borderRadius: "1.5rem",
    backgroundColor: "#f97316",
    color: "white",
    boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.1)",
    border: "1px solid #fb923c",
    transition: "transform",
  },
  bentoLabel: {
    color: "#94a3b8",
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontSize: "11px",
    fontFamily: "monospace",
  },
  bentoLabelOrange: {
    color: "#fed7aa",
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    fontSize: "10px",
    fontFamily: "monospace",
  },
  bentoStatDark: {
    fontSize: "2.25rem",
    fontWeight: "900",
    color: "#020617",
  },
  bentoStatOrange: {
    fontSize: "2.25rem",
    fontWeight: "900",
    color: "#FF5F14",
  },
  bentoStatWhite: {
    fontSize: "2.25rem",
    fontWeight: "900",
    color: "white",
  },
  bentoSubtext: {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "bold",
    marginTop: "4px",
    margin: 0,
  },
  bentoSubtextOrange: {
    fontSize: "11px",
    color: "#ffedd5",
    fontWeight: "600",
    marginTop: "4px",
    margin: 0,
  },
  pillarsWrapper: {
    marginBottom: "7rem",
    zIndex: 10,
  },
  eyebrow: {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#FF5F14",
    fontWeight: "900",
  },
  sectionTitle: {
    fontWeight: "900",
    color: "#020617",
    marginTop: "0.25rem",
    marginBottom: "0.5rem",
  },
  sectionSubtext: {
    fontSize: "0.875rem",
    color: "#64748b",
    fontWeight: "600",
    maxWidth: "42rem",
    margin: "0 auto",
  },
  tabGroup: {
    padding: "0.375rem",
    borderRadius: "1rem",
    backgroundColor: "#f1f5f9",
    border: "1px solid rgba(226, 232, 240, 0.5)",
    gap: "0.25rem",
  },
  tabButton: {
    padding: "0.625rem 1.25rem",
    borderRadius: "0.75rem",
    transition: "all 0.3s",
    fontWeight: "900",
    fontSize: "0.75rem",
    border: "none",
    background: "transparent",
    cursor: "pointer",
  },
  tabButtonActive: {
    backgroundColor: "#FF5F14",
    color: "white",
    boxShadow: "0 4px 6px -1px rgba(249, 115, 22, 0.15)",
  },
  tabButtonInactive: {
    color: "#64748b",
  },
  showcaseCard: {
    borderRadius: "1.5rem",
    backgroundColor: "white",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 10px 40px rgba(0,0,0,0.03)",
    overflow: "hidden",
    margin: 0,
  },
  showcaseCorner: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "8rem",
    height: "8rem",
    backgroundColor: "rgba(249, 115, 22, 0.05)",
    borderBottomLeftRadius: "100%",
    pointerEvents: "none",
  },
  showcaseIconBox: {
    width: "3.5rem",
    height: "3.5rem",
    borderRadius: "1rem",
    backgroundColor: "#fff7ed",
    border: "1px solid rgba(255, 237, 213, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
  },
  showcaseTagline: {
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontWeight: "900",
    color: "#FF5F14",
  },
  showcaseTitle: {
    fontSize: "1.875rem",
    fontWeight: "900",
    color: "#020617",
    lineHeight: 1,
    marginTop: "0.25rem",
    marginBottom: 0,
  },
  showcaseSubtitle: {
    color: "#64748b",
    fontWeight: "600",
    fontSize: "0.875rem",
    lineHeight: 1.625,
    borderLeft: "2px solid rgba(249, 115, 22, 0.4)",
    paddingLeft: "1rem",
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    margin: 0,
  },
  bulletCheckWrapper: {
    backgroundColor: "#ecfdf5",
    borderRadius: "50%",
    padding: "2px",
    marginTop: "2px",
  },
  statBlock: {
    padding: "2rem",
    borderRadius: "1rem",
    backgroundColor: "#f8fafc",
    border: "1px solid rgba(226, 232, 240, 0.5)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    minHeight: "200px",
    overflow: "hidden",
  },
  statBlockBadgeWrapper: {
    position: "absolute",
    top: "1rem",
    right: "1rem",
  },
  statBlockBadge: {
    padding: "0.25rem 0.625rem",
    borderRadius: "0.375rem",
    backgroundColor: "#ecfdf5",
    border: "1px solid #d1fae5",
    color: "#059669",
    fontSize: "9.5px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontFamily: "monospace",
  },
  statBlockLabel: {
    fontSize: "11px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#FF5F14",
    fontFamily: "monospace",
  },
  statBlockValue: {
    fontSize: "2rem",
    fontWeight: "900",
    color: "#020617",
    lineHeight: 1.1,
  },
  statBlockDesc: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: "bold",
    lineHeight: 1.625,
    margin: 0,
  },
  trustBanner: {
    marginBottom: "7rem",
    padding: "3rem",
    borderRadius: "1.5rem",
    backgroundColor: "#020617",
    color: "white",
    border: "1px solid #1e293b",
    boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  trustGlow1: {
    position: "absolute",
    top: "-3rem",
    left: "-3rem",
    width: "16rem",
    height: "16rem",
    backgroundColor: "rgba(255, 95, 20, 0.1)",
    borderRadius: "50%",
    filter: "blur(80px)",
    pointerEvents: "none",
  },
  trustGlow2: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: "24rem",
    height: "24rem",
    backgroundColor: "rgba(249, 115, 22, 0.05)",
    borderRadius: "50%",
    filter: "blur(100px)",
    pointerEvents: "none",
  },
  trustEyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: "0.25rem",
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    backgroundColor: "rgba(249, 115, 22, 0.1)",
    border: "1px solid rgba(249, 115, 22, 0.2)",
    color: "#FF5F14",
    fontSize: "11px",
    fontWeight: "900",
    fontFamily: "monospace",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  trustTitle: {
    fontSize: "1.875rem",
    fontWeight: "900",
    lineHeight: 1.1,
    margin: 0,
    color: "white",
  },
  trustText: {
    fontSize: "0.875rem",
    color: "#94a3b8",
    fontWeight: "600",
    lineHeight: 1.625,
    margin: 0,
  },
  trustCard: {
    padding: "1.25rem",
    borderRadius: "1rem",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    transition: "border-color 0.3s",
  },
  trustCardTitle: {
    fontSize: "0.75rem",
    fontWeight: "900",
    color: "white",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.375rem",
  },
  trustCardText: {
    fontSize: "10px",
    color: "#94a3b8",
    fontWeight: "bold",
    lineHeight: 1.625,
    margin: 0,
  },
  storyWrapper: {
    marginBottom: "7rem",
    zIndex: 10,
  },
  storyEyebrow: {
    padding: "0.25rem 0.75rem",
    borderRadius: "9999px",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#FF5F14",
    fontSize: "12px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontFamily: "monospace",
  },
  storyTitle: {
    fontSize: "2.5rem",
    fontWeight: "900",
    color: "#020617",
    lineHeight: 1.1,
    margin: 0,
  },
  storyMainText: {
    fontSize: "1rem",
    color: "#475569",
    fontWeight: "600",
    lineHeight: 1.625,
    margin: 0,
  },
  storySubText: {
    fontSize: "0.875rem",
    color: "#64748b",
    fontWeight: "500",
    lineHeight: 1.625,
    margin: 0,
  },
  storyFeatureTitle: {
    fontSize: "0.75rem",
    fontWeight: "900",
    color: "#020617",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.125rem",
  },
  storyFeatureText: {
    fontSize: "11px",
    color: "#64748b",
    fontWeight: "600",
    margin: 0,
  },
  solutionCard: {
    padding: "1.5rem",
    borderRadius: "1.5rem",
    backgroundColor: "white",
    border: "1px solid rgba(226, 232, 240, 0.7)",
    boxShadow: "0 4px 25px rgba(0,0,0,0.015)",
    overflow: "hidden",
  },
  solutionCorner: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "6rem",
    height: "6rem",
    backgroundColor: "rgba(249, 115, 22, 0.03)",
    borderBottomLeftRadius: "100%",
    pointerEvents: "none",
  },
  solutionIconBox: {
    width: "3rem",
    height: "3rem",
    borderRadius: "1rem",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  solutionTitle: {
    fontSize: "0.875rem",
    fontWeight: "900",
    color: "#FF5F14",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.25rem",
  },
  solutionText: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: "bold",
    lineHeight: 1.625,
    margin: 0,
  },
  validationBanner: {
    padding: "1.25rem",
    borderRadius: "1rem",
    backgroundColor: "rgba(249, 115, 22, 0.05)",
    border: "1px solid rgba(249, 115, 22, 0.1)",
    gap: "0.875rem",
  },
  validationTitle: {
    fontSize: "11px",
    fontWeight: "900",
    color: "#020617",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "0.25rem",
  },
  validationText: {
    fontSize: "10.5px",
    color: "#475569",
    fontWeight: "bold",
    lineHeight: 1.625,
    margin: 0,
  },
  governanceWrapper: {
    marginBottom: "7rem",
    borderTop: "1px solid rgba(226, 232, 240, 0.8)",
    paddingTop: "5rem",
    zIndex: 10,
  },
  leadershipCard: {
    padding: "2.5rem",
    borderRadius: "1.5rem",
    backgroundColor: "white",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 4px 20px rgba(0,0,0,0.02)",
    overflow: "hidden",
  },
  leadershipCorner: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "8rem",
    height: "8rem",
    backgroundColor: "rgba(249, 115, 22, 0.05)",
    borderBottomLeftRadius: "100%",
    pointerEvents: "none",
  },
  leadershipLabel: {
    fontSize: "11px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#FF5F14",
    fontFamily: "monospace",
    display: "block",
    marginBottom: "0.5rem",
  },
  leadershipName: {
    fontSize: "1.875rem",
    fontWeight: "900",
    color: "#020617",
    margin: 0,
  },
  leadershipRole: {
    fontSize: "0.75rem",
    fontWeight: "bold",
    color: "#64748b",
    fontFamily: "monospace",
    marginTop: "0.25rem",
    margin: 0,
  },
  leadershipBio: {
    fontSize: "0.875rem",
    color: "#475569",
    fontWeight: "600",
    lineHeight: 1.625,
    borderLeft: "2px solid rgba(249, 115, 22, 0.4)",
    paddingLeft: "1rem",
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    margin: 0,
  },
  linkedinButton: {
    gap: "0.5rem",
    padding: "0.625rem 1.25rem",
    borderRadius: "0.75rem",
    backgroundColor: "#0077B5",
    color: "white",
    fontSize: "0.75rem",
    fontWeight: "bold",
    boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    textDecoration: "none",
  },
  registryCard: {
    padding: "2.5rem",
    borderRadius: "1.5rem",
    backgroundColor: "#0f172a",
    color: "white",
    border: "1px solid #1e293b",
    boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  registryCorner: {
    position: "absolute",
    top: 0,
    right: 0,
    width: "6rem",
    height: "6rem",
    backgroundColor: "rgba(249, 115, 22, 0.1)",
    borderBottomLeftRadius: "100%",
    pointerEvents: "none",
  },
  registryLabel: {
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#FF5F14",
    fontFamily: "monospace",
    display: "block",
    marginBottom: "0.25rem",
  },
  registryTitle: {
    fontSize: "1.25rem",
    fontWeight: "900",
    color: "white",
    margin: 0,
  },
  registryIconBox: {
    width: "2rem",
    height: "2rem",
    borderRadius: "0.5rem",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "2px",
  },
  registryFieldLabel: {
    fontSize: "9px",
    fontWeight: "bold",
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    display: "block",
  },
  registryFieldValue: {
    fontSize: "0.875rem",
    fontWeight: "900",
    color: "white",
    marginTop: "0.125rem",
    margin: 0,
  },
  registryAddress: {
    fontSize: "0.75rem",
    color: "#cbd5e1",
    fontWeight: "600",
    lineHeight: 1.625,
    marginTop: "0.125rem",
    margin: 0,
  },
  registryEmail: {
    fontSize: "0.75rem",
    fontWeight: "600",
    color: "#fb923c",
    textDecoration: "none",
    marginTop: "0.125rem",
    display: "block",
  },
  statusBadge: {
    gap: "0.375rem",
    padding: "0.25rem 0.625rem",
    borderRadius: "0.375rem",
    backgroundColor: "rgba(16, 185, 129, 0.1)",
    border: "1px solid rgba(16, 185, 129, 0.2)",
    color: "#34d399",
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    fontFamily: "monospace",
  },
  registryFooterLabel: {
    fontSize: "10px",
    fontWeight: "900",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#FF5F14",
    fontFamily: "monospace",
    display: "block",
    marginBottom: "0.25rem",
  },
  registryFooterText: {
    fontSize: "11px",
    color: "#94a3b8",
    fontWeight: "600",
    lineHeight: 1.625,
    margin: 0,
  },
  milestonesWrapper: {
    borderTop: "1px solid rgba(226, 232, 240, 0.8)",
    paddingTop: "5rem",
    marginBottom: "3rem",
    zIndex: 10,
  },
  milestoneCard: {
    padding: "1.5rem",
    borderRadius: "1rem",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    backgroundColor: "#fff",
  },
  milestoneTopBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    backgroundColor: "#FF5F14",
  },
  milestoneYear: {
    fontSize: "1.5rem",
    fontWeight: 900,
    color: "#FF5F14",
  },
  milestoneTag: {
    fontSize: "9px",
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    backgroundColor: "#fff7ed",
    border: "1px solid #ffedd5",
    color: "#FF5F14",
    padding: "2px 8px",
    borderRadius: "4px",
  },
  milestoneTitle: {
    fontSize: "0.875rem",
    fontWeight: 900,
    color: "#020617",
    marginBottom: "0.5rem",
  },
  milestoneDesc: {
    fontSize: "0.75rem",
    color: "#64748b",
    fontWeight: 600,
    lineHeight: 1.625,
  },
};
